import React, { useState, useEffect } from 'react';
import './RoundsSetup.css';

const RoundsSetup = () => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editRoundId, setEditRoundId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    roundName: '',
    roundCode: '',
    displayName: '',
    description: '',
    isActive: true
  });

  // Fetch rounds on component mount
  useEffect(() => {
    fetchRounds();
  }, []);

  const fetchRounds = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('Token') || localStorage.getItem('token');

      if (!token) {
        setError('No authentication token found. Please login again.');
        setLoading(false);
        return;
      }

      console.log('Fetching rounds...');

      const response = await fetch(
        'https://api.asterickx.com/api/Rounds',
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          setError('Unauthorized. Your session may have expired. Please login again.');
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      console.log('Rounds fetched:', data);
      setRounds(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching rounds:', err);
      setError(err.message || 'Failed to load rounds. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('Token') || localStorage.getItem('token');

      if (!token) {
        alert('No authentication token found. Please login again.');
        return;
      }

      const submitData = {
        roundName: formData.roundName,
        roundCode: formData.roundCode,
        displayName: formData.displayName,
        description: formData.description,
        isActive: formData.isActive
      };

      console.log('Submitting round data:', submitData);

      const url = isEdit
        ? `https://api.asterickx.com/api/Rounds/${editRoundId}`
        : 'https://api.asterickx.com/api/Rounds';

      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert('Unauthorized. Please login again.');
          return;
        } else if (response.status === 400) {
          const errorData = await response.json().catch(() => ({}));
          console.error('400 Error Response:', errorData);

          let errorMessage = 'Bad request. Please check all fields are filled correctly.';
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const errors = Object.values(errorData.errors).flat();
            errorMessage = errors.join('\n');
          }

          alert(`Error: ${errorMessage}`);
          return;
        }
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} round`);
      }

      resetForm();
      fetchRounds();
      alert(`Round ${isEdit ? 'updated' : 'created'} successfully!`);
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} round:`, err);
      alert(err.message || `Failed to ${isEdit ? 'update' : 'create'} round. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (round) => {
    setIsEdit(true);
    setEditRoundId(round.id || round.roundId);
    setFormData({
      roundName: round.roundName || '',
      roundCode: round.roundCode || round.code || '',
      displayName: round.displayName || '',
      description: round.description || '',
      isActive: round.isActive !== undefined ? round.isActive : true
    });
    setShowModal(true);
  };

  const handleDelete = async (roundId) => {
    if (!window.confirm('Are you sure you want to delete this round?')) {
      return;
    }

    try {
      const token = localStorage.getItem('Token') || localStorage.getItem('token');

      if (!token) {
        alert('No authentication token found. Please login again.');
        return;
      }

      const response = await fetch(
        `https://api.asterickx.com/api/Rounds/${roundId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete round');
      }

      fetchRounds();
      alert('Round deleted successfully!');
    } catch (err) {
      console.error('Error deleting round:', err);
      alert('Failed to delete round. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      roundName: '',
      roundCode: '',
      displayName: '',
      description: '',
      isActive: true
    });
    setShowModal(false);
    setIsEdit(false);
    setEditRoundId(null);
  };

  const filteredRounds = rounds.filter(round => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (round.roundName?.toLowerCase().includes(searchLower)) ||
      (round.roundCode?.toLowerCase().includes(searchLower)) ||
      (round.displayName?.toLowerCase().includes(searchLower)) ||
      (round.description?.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="rounds-setup-container">
      {/* Header Section */}
      <div className="header-section">
        <h2 className="page-title">
          <i className="fas fa-layer-group me-2"></i>
          Rounds Setup
        </h2>

        <div className="right-controls">
          {/* Search Box */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search rounds..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <svg
              className="search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>

          {/* Add New Round Button */}
          <button
            className="add-round-btn"
            onClick={() => {
              setIsEdit(false);
              resetForm();
              setShowModal(true);
            }}
          >
            <span className="btn-icon">+</span>
            <span>Add New Round</span>
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <p className="error-message">{error}</p>
          {error.includes('login') && (
            <button
              className="retry-btn"
              onClick={() => window.location.href = '/login'}
            >
              Go to Login
            </button>
          )}
        </div>
      )}

      {/* Search Results Info */}
      {searchTerm && (
        <div className="search-results-info">
          Showing {filteredRounds.length} of {rounds.length} rounds
        </div>
      )}

      {/* Rounds Table */}
      <div className="table-container">
        <table className="rounds-table">
          <thead>
            <tr>
              <th>Round Name</th>
              <th>Round Code</th>
              <th>Display Name</th>
              <th>Description</th>
              {/* <th>Status</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                {/* <td colSpan="6" className="text-center"> */}
                <td colSpan="5" className="text-center">
                  <div className="loader"></div>
                  Loading rounds...
                </td>
              </tr>
            ) : filteredRounds.length > 0 ? (
              filteredRounds.map((round, index) => (
                <tr key={round.id || round.roundId || index}>
                  <td className="round-name-cell">
                    <strong>{round.roundName || 'N/A'}</strong>
                  </td>
                  <td>
                    <span className="round-code-badge">
                      {round.roundCode || round.code || 'N/A'}
                    </span>
                  </td>
                  <td>{round.displayName || 'N/A'}</td>
                  <td className="description-cell">
                    {round.description || '-'}
                  </td>
                  {/* <td>
                    <span className={`status-badge ${round.isActive ? 'active' : 'inactive'}`}>
                      {round.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td> */}
                  <td>
                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(round)}
                        title="Edit Round"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(round.id || round.roundId)}
                        title="Delete Round"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  {searchTerm ? 'No rounds found matching your search' : 'No rounds available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for Add/Edit Round */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') {
            resetForm();
          }
        }}>
          <div className="modal-container">
            <div className="modal-header-custom">
              <h3>
                <i className="fas fa-layer-group me-2"></i>
                {isEdit ? 'Edit Round' : 'Add New Round'}
              </h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>

            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* Round Name */}
                  <div className="form-group">
                    <label>Round Name *</label>
                    <input
                      type="text"
                      name="roundName"
                      value={formData.roundName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter round name (e.g., Quarter Final)"
                    />
                  </div>

                  {/* Round Code */}
                  <div className="form-group">
                    <label>Round Code *</label>
                    <input
                      type="text"
                      name="roundCode"
                      value={formData.roundCode}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter round code (e.g., QF)"
                    />
                    <small className="field-hint">Short code for the round (e.g., QF, SF, F)</small>
                  </div>

                  {/* Display Name */}
                  <div className="form-group">
                    <label>Display Name *</label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter display name"
                    />
                    <small className="field-hint">Name shown to users</small>
                  </div>

                  {/* Description */}
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter round description (optional)"
                    />
                  </div>

                  {/* Is Active */}
                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span className="checkbox-text">Active</span>
                    </label>
                    <small className="field-hint">Only active rounds will be available for selection</small>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </>
                    ) : (
                      isEdit ? 'Update Round' : 'Create Round'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoundsSetup;